<template>
  <div class="form-file-upload">
    <div
      class="form-file-upload__dropzone"
      :class="{
        'form-file-upload__dropzone--active': isDragOver,
        'form-file-upload__dropzone--disabled': isLimitReached,
      }"
      role="button"
      tabindex="0"
      :aria-disabled="isLimitReached"
      @click="openFileDialog"
      @keydown.enter.prevent="openFileDialog"
      @keydown.space.prevent="openFileDialog"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <p class="form-file-upload__dropzone-text">
        <button type="button" class="form-file-upload__dropzone-link" @click.stop="openFileDialog">
          <span class="form-file-upload__dropzone-desktop-text">Выберите</span>
          <span class="form-file-upload__dropzone-mobile-text">Выбрать файлы</span>
        </button>
        <span class="form-file-upload__dropzone-desktop-text"> или перетащите файлы</span>
      </p>
      <p class="form-file-upload__dropzone-hint form-file-upload__dropzone-hint--desktop">
        Допустимы только PDF, DOC, XLS, PPT до 100 Мб, не более 5 файлов
      </p>
      <p class="form-file-upload__dropzone-hint form-file-upload__dropzone-hint--mobile">
        PDF, DOC, XLS до 100 Мб
      </p>
      <input
        ref="fileInputRef"
        type="file"
        class="form-file-upload__input"
        multiple
        :accept="acceptAttribute"
        @change="onInputChange"
      />
    </div>

    <ul v-if="items.length" class="form-file-upload__list">
      <li
        v-for="item in items"
        :key="item.id"
        class="form-file-upload__item"
        :class="{
          'form-file-upload__item--error': item.status === 'error',
          'form-file-upload__item--uploading': item.status === 'uploading',
        }"
      >
        <div class="form-file-upload__item-icon">
          <a-spin v-if="item.status === 'uploading'" size="small" />
          <img v-else :src="getDocFormatIconPath(item.file.name)" />
        </div>

        <div class="form-file-upload__item-body">
          <p class="form-file-upload__item-name">Файл. {{ item.file.name }}</p>
          <p class="form-file-upload__item-meta">
            {{ getFileFormatLabel(item.file.name) }} / {{ formatFileSize(item.file.size) }}
          </p>
          <div
            v-if="item.status === 'uploading'"
            class="form-file-upload__progress"
            aria-hidden="true"
          >
            <span class="form-file-upload__progress-bar" :style="{ width: `${item.progress}%` }" />
          </div>
          <p
            v-else-if="item.status === 'error' && item.errorMessage"
            class="form-file-upload__item-error"
          >
            {{ item.errorMessage }}
          </p>
        </div>

        <button
          type="button"
          class="form-file-upload__remove"
          aria-label="Удалить файл"
          :disabled="item.status === 'uploading'"
          @click="removeItem(item.id)"
        >
          <img
            :src="deleteFileIconPath"
            class="form-file-upload__remove-icon"
            width="24"
            height="24"
          />
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { message } from 'ant-design-vue';
import { computed, ref } from 'vue';
import {
  formatFileSize,
  getDeleteFileIconPath,
  getDocFormatIconPath,
  getFileExtension,
  getFileFormatLabel,
  isAllowedUploadExtension,
  FILE_UPLOAD_MAX_SIZE_BYTES,
} from '@/js/utils/file-upload-utils';

export type FileUploadItemStatus = 'uploading' | 'ready' | 'error';

export type FileUploadItem = {
  id: string;
  file: File;
  status: FileUploadItemStatus;
  progress: number;
  errorMessage?: string;
};

const props = withDefaults(
  defineProps<{
    modelValue?: FileUploadItem[];
  }>(),
  {
    modelValue: () => [],
  },
);

const emit = defineEmits<{
  'update:modelValue': [items: FileUploadItem[]];
}>();

const acceptAttribute =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
const deleteFileIconPath = getDeleteFileIconPath();
const MAX_UPLOAD_FILES = 5;
const maxFilesErrorMessage = `Можно загрузить не более ${MAX_UPLOAD_FILES} файлов`;

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragOver = ref(false);
const uploadTimers = new Map<string, number>();

const items = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});
const isLimitReached = computed(() => items.value.length >= MAX_UPLOAD_FILES);

function createItemId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function validateFile(file: File): { valid: boolean; errorMessage?: string } {
  const extension = getFileExtension(file.name);

  if (!isAllowedUploadExtension(extension)) {
    return { valid: false };
  }

  if (file.size > FILE_UPLOAD_MAX_SIZE_BYTES) {
    return {
      valid: false,
      errorMessage: 'Размер файла не должен превышать 100 Мб',
    };
  }

  return { valid: true };
}

function clearUploadTimer(id: string): void {
  const timerId = uploadTimers.get(id);
  if (timerId !== undefined) {
    window.clearInterval(timerId);
    uploadTimers.delete(id);
  }
}

function simulateUpload(item: FileUploadItem): void {
  const durationMs = 600;
  const startedAt = Date.now();

  const timerId = window.setInterval(() => {
    const elapsed = Date.now() - startedAt;
    const progress = Math.min(100, Math.round((elapsed / durationMs) * 100));
    const nextItems = items.value.map((entry) =>
      entry.id === item.id ? { ...entry, progress } : entry,
    );
    items.value = nextItems;

    if (progress >= 100) {
      clearUploadTimer(item.id);
      items.value = items.value.map((entry) =>
        entry.id === item.id ? { ...entry, status: 'ready' as const, progress: 100 } : entry,
      );
    }
  }, 50);

  uploadTimers.set(item.id, timerId);
}

function addFiles(fileList: FileList | File[]): void {
  const incoming = Array.from(fileList);
  if (!incoming.length) return;

  const availableSlots = MAX_UPLOAD_FILES - items.value.length;
  if (availableSlots <= 0) {
    message.warning(maxFilesErrorMessage);
    return;
  }

  if (incoming.length > availableSlots) {
    message.warning(maxFilesErrorMessage);
  }

  const filesToAdd = incoming.slice(0, availableSlots);
  const nextItems = [...items.value];
  const uploadsToStart: FileUploadItem[] = [];

  filesToAdd.forEach((file) => {
    const validation = validateFile(file);
    const id = createItemId();

    if (!validation.valid) {
      nextItems.push({
        id,
        file,
        status: 'error',
        progress: 0,
        errorMessage: validation.errorMessage,
      });
      return;
    }

    const item: FileUploadItem = {
      id,
      file,
      status: 'uploading',
      progress: 0,
    };
    nextItems.push(item);
    uploadsToStart.push(item);
  });

  items.value = nextItems;
  uploadsToStart.forEach((item) => simulateUpload(item));
}

function openFileDialog(): void {
  if (isLimitReached.value) {
    message.warning(maxFilesErrorMessage);
    return;
  }

  fileInputRef.value?.click();
}

function onInputChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) {
    addFiles(input.files);
  }
  input.value = '';
}

function onDrop(event: DragEvent): void {
  isDragOver.value = false;
  if (event.dataTransfer?.files?.length) {
    addFiles(event.dataTransfer.files);
  }
}

function onDragEnter(): void {
  if (isLimitReached.value) return;
  isDragOver.value = true;
}

function onDragLeave(event: DragEvent): void {
  const related = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement;
  if (related && current.contains(related)) return;
  isDragOver.value = false;
}

function removeItem(id: string): void {
  clearUploadTimer(id);
  items.value = items.value.filter((item) => item.id !== id);
}

function reset(): void {
  items.value.forEach((item) => clearUploadTimer(item.id));
  items.value = [];
}

function getReadyFiles(): File[] {
  return items.value.filter((item) => item.status === 'ready').map((item) => item.file);
}

function setSubmittingState(isSubmitting: boolean): void {
  if (!isSubmitting) return;

  items.value = items.value.map((item) =>
    item.status === 'ready' ? { ...item, status: 'uploading' as const, progress: 0 } : item,
  );

  items.value
    .filter((item) => item.status === 'uploading' && item.progress === 0)
    .forEach((item) => simulateUpload(item));
}

function resetSubmittingState(): void {
  items.value.forEach((item) => clearUploadTimer(item.id));
  items.value = items.value.map((item) =>
    item.status === 'uploading' ? { ...item, status: 'ready' as const, progress: 100 } : item,
  );
}

defineExpose({
  reset,
  getReadyFiles,
  setSubmittingState,
  resetSubmittingState,
});
</script>

<style scoped src="./form-file-upload.css"></style>
